import { Link } from "react-router-dom";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Lock, Users, ArrowRight, Clock, Zap, LifeBuoy } from "lucide-react";

/**
 * Out-of-credits paywall. Apr-25 2026 Scarcity pivot — copy reflects the
 * new weekly top-up model (2 free credits every 7 days). When we know
 * how many days remain in the cycle, we surface that explicitly so the
 * scarcity message reads concrete, not vague.
 */
export default function DailyLimitModal({ open, onOpenChange, onOpenCheckout, daysUntilReset }) {
  // Render-friendly countdown — handles "today", "tomorrow", and N days.
  const cycleCopy = (() => {
    if (daysUntilReset === null || daysUntilReset === undefined) {
      return "Your next free credits arrive on the next weekly cycle.";
    }
    if (daysUntilReset <= 0) return "Your next free credits arrive today.";
    if (daysUntilReset === 1) return "Your next free credits arrive tomorrow.";
    return `Your next free credits arrive in ${daysUntilReset} days.`;
  })();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="glass border-white/10 max-w-md" data-testid="daily-limit-modal">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2 text-2xl tracking-tight">
            <Lock className="text-[#ffb067]" size={20} /> Out of Credits
          </DialogTitle>
          <DialogDescription className="text-white/60 text-sm pt-1" data-testid="daily-limit-cycle-copy">
            {cycleCopy} <strong className="text-white">Upgrade now for instant access.</strong>
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 pt-3" data-testid="daily-limit-actions">
          <div className="aspect-[16/7] rounded-xl bg-gradient-to-br from-[#FF7B00]/35 via-[#5D3FD3]/35 to-[#0044FF]/30 grid place-items-center border border-white/10 relative overflow-hidden">
            <div className="absolute inset-0 sheen opacity-25" />
            <div className="text-center z-10 px-4">
              <div className="label-eyebrow mb-1">Unlimited access</div>
              <div className="font-display text-lg leading-tight">More credits · priority AI · no caps</div>
            </div>
          </div>

          <button
            onClick={() => {
              onOpenChange(false);
              onOpenCheckout?.();
            }}
            className="w-full btn-primary rounded-xl py-3.5 font-display font-semibold text-[15px] flex items-center justify-center gap-2 shadow-[0_12px_40px_-12px_rgba(0,68,255,0.55)]"
            data-testid="btn-goto-upgrade"
          >
            <Zap size={15} /> Upgrade · Pay with Razorpay
            <ArrowRight size={14} />
          </button>

          <Link
            to="/referrals"
            onClick={() => onOpenChange(false)}
            className="w-full rounded-xl py-3 font-display font-medium border border-white/10 bg-white/[0.02] hover:bg-white/5 text-white/70 hover:text-white flex items-center justify-center gap-2 transition"
            data-testid="btn-goto-referrals"
          >
            <Users size={14} /> Or invite friends · earn credits free
          </Link>

          <button
            type="button"
            onClick={() => {
              onOpenChange(false);
              try {
                window.dispatchEvent(new CustomEvent("oak:open-guide", {
                  detail: { prefill: "I just ran out of credits — what are all my options to keep learning today?" },
                }));
              } catch {
                /* CustomEvent unsupported → silently no-op */
              }
            }}
            className="w-full text-[12px] text-[#9bffcc] hover:text-[#00FF9D] flex items-center justify-center gap-1.5 pt-0.5 transition"
            data-testid="btn-ask-oak-guide"
          >
            <LifeBuoy size={12} /> Confused about credits? Ask OAK Guide
          </button>

          <div className="flex items-center justify-center gap-1.5 text-[11px] text-white/45 pt-1" data-testid="daily-limit-cycle-hint">
            <Clock size={11} /> Free credits top up to 2 every 7 days
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
